import { CardProps } from '@/components/ui/Cards/BaseCard';
import classnames from 'classnames';
import DropdownMenu from '@/components/ui/DropdownMenu';
import IconButton from '@/components/ui/IconButton';
import Link from '@/components/ui/Link';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import Thumbnail from '../Thumbnail';
import DotSeparatedContent from '../DotSeparatedContent';
import { useDarkModeContext } from '../PDP/DarkModeContext/useDarkModeContext';

const BaseCardList = ({
  labelDetail,
  heading,
  href,
  buttonOptions,
  size,
  thumbnailProps,
  headingSmall,
  cardType,
  onClickCard,
  programVariant
}: CardProps) => {
  const isLarge = size === 'large';
  let ratio;
  const xSmallWindow = useMediaQuery(ScreenSizeQueries.xsOnly);
  const smallWindow = useMediaQuery(ScreenSizeQueries.sm);
  const mediumWindow = useMediaQuery(ScreenSizeQueries.md);
  const darkmodeContext = useDarkModeContext();
  const isDarkMode = darkmodeContext?.isDarkMode ?? false;

  if (smallWindow || mediumWindow) {
    ratio = 3 / 2;
  } else if (smallWindow || xSmallWindow) {
    ratio = 1;
  }

  const contentContainerClasses = classnames('pr-6lg:pr-8 flex items-center gap-5', {
    'group mx-[-24px] bg-charcoal px-6 transition hover:bg-gray-dark focus:bg-gray-dark md:mx-[-48px] md:px-12':
      isDarkMode,
    'bg-white transition hover:bg-gray-light focus:bg-gray-light': !isDarkMode
  });

  const imageContainerClasses = classnames(
    'group rounded',
    'xs:max-h-[70px] my-[25px] min-w-[70px] sm:min-w-[168px] md:max-h-[112px] md:max-w-[168px] lg:max-w-[302px]'
  );

  const headingClasses = classnames(
    '!fixed-size-label no-underline',
    {
      'pb-3.5 sm:!text-sm lg:!heading-6-medium sm:pb-4': isLarge,
      'md:!heading-6-medium': !isLarge
    },
    {
      'text-white hover:text-silver': isDarkMode,
      'text-black hover:text-red': !isDarkMode
    }
  );

  const labelClasses = classnames('text-2', {
    'text-silver': isDarkMode,
    'text-gray-dark': !isDarkMode
  });

  const labelDetailClasses = classnames('label', {
    'text-silver': isDarkMode,
    'text-gray-dark': !isDarkMode
  });

  return (
    <div className={contentContainerClasses} data-component={'BaseCardList'}>
      {isLarge ? (
        <div className={classnames('flex w-full flex-col items-start gap-1 py-6')}>
          <Link as="a" href={href} className={headingClasses} text={heading} />
          <span className={labelClasses}>
            {' '}
            <DotSeparatedContent content={[labelDetail, headingSmall]} />
          </span>
        </div>
      ) : (
        <div className={classnames('flex w-full flex-col items-start gap-1')}>
          <span className={labelDetailClasses}>{labelDetail}</span>
          <Link as="a" href={href} className={headingClasses} text={heading} />
        </div>
      )}

      {isLarge ? (
        <Thumbnail
          ratio={ratio}
          icon={thumbnailProps?.iconThumbnail}
          className={imageContainerClasses}
          rounded={true}
          card={true}
          onClick={onClickCard}
          {...thumbnailProps}
          imageSizes={{
            xs: '70px',
            md: '168px',
            lg: '302px'
          }}
          fitImage={cardType !== 'publication'}
          isListFormat={xSmallWindow || smallWindow}
          programVariant={programVariant}
          forceIcon={isDarkMode}
        />
      ) : (
        <DropdownMenu
          button={() => <IconButton size="large" label="More options" name="more-ios" />}
          align="end"
          items={buttonOptions}
        />
      )}
    </div>
  );
};

export default BaseCardList;
